import { useState } from "react";
import { FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { AuthStatus } from "@/components/layout/AuthStatus";
import {
  headerButtonClassName,
  headerToolClassName,
} from "@/components/layout/headerButtonClassName";
import { AttachmentsHeaderButton } from "@/components/attachments/AttachmentsHeaderButton";
import { FlashcardsHeaderButton } from "@/components/flashcards/FlashcardsHeaderButton";
import { GlossaryHeaderButton } from "@/components/glossary/GlossaryHeaderButton";
import { HelpHeaderButton } from "@/components/help/HelpHeaderButton";
import { legalNotices } from "@/components/legal/legalNotices";
import { Button } from "@/components/ui/button";
import { ProgressRing } from "@/components/ui/progress-ring";
import { useHeaderRoute } from "@/hooks/layout/useHeaderRoute";

const headerProgressColor = "var(--primary-foreground)";
const headerProgressTrackColor =
  "color-mix(in oklab, var(--primary-foreground) 30%, transparent)";

type HeaderMenu = "help" | "account";

/**
 * Progress, course tools, and account on the right side of the header.
 */
const HeaderToolbar = () => {
  const [openMenu, setOpenMenu] = useState<HeaderMenu | null>(null);
  const {
    progressPercent,
    enrollmentId,
    showProgress,
    showFlashcardsButton,
    showAttachmentsButton,
    showHelp,
    showImpressum,
    showGlossary,
    showHeaderDivider,
  } = useHeaderRoute();

  const setHelpOpen = (open: boolean) => {
    setOpenMenu(open ? "help" : null);
  };

  const setAccountOpen = (open: boolean) => {
    setOpenMenu(open ? "account" : null);
  };

  const courseToolsVisible =
    showFlashcardsButton || showGlossary || showAttachmentsButton;

  const renderCourseTools = () => {
    if (!courseToolsVisible) {
      return null;
    }

    return (
      <nav aria-label="Kurswerkzeuge" className="flex items-center gap-0.5">
        {showFlashcardsButton && enrollmentId && (
          <FlashcardsHeaderButton enrollmentId={enrollmentId} />
        )}
        {showGlossary && <GlossaryHeaderButton />}
        {showAttachmentsButton && enrollmentId && (
          <AttachmentsHeaderButton enrollmentId={enrollmentId} />
        )}
      </nav>
    );
  };

  const renderHelp = () => {
    if (!showHelp) {
      return null;
    }

    return (
      <HelpHeaderButton open={openMenu === "help"} onOpenChange={setHelpOpen} />
    );
  };

  const renderProgress = () => {
    if (!showProgress) {
      return null;
    }

    const percent = Math.round(progressPercent);

    return (
      <span
        role="img"
        aria-label={`Kursfortschritt ${percent} Prozent`}
        className="hidden sm:inline-flex"
      >
        <ProgressRing
          percentage={progressPercent}
          size="md"
          showLabel
          fillColor={headerProgressColor}
          labelColor={headerProgressColor}
          trackColor={headerProgressTrackColor}
        />
      </span>
    );
  };

  const renderLoginImpressum = () => {
    if (!showImpressum) {
      return null;
    }

    return (
      <Button
        variant="outline"
        size="sm"
        className={[
          "border-transparent text-primary-foreground",
          headerButtonClassName,
          headerToolClassName(false),
        ].join(" ")}
        asChild
      >
        <Link to={legalNotices.impressum.path}>
          <FileText className="size-4" />
          {legalNotices.impressum.title}
        </Link>
      </Button>
    );
  };

  return (
    <div className="flex shrink-0 items-center gap-4">
      {renderProgress()}
      <div className="flex items-center">
        <div className="flex items-center gap-0.5">
          {renderCourseTools()}
          {renderHelp()}
        </div>
        {renderLoginImpressum()}
        {showHeaderDivider && (
          <span
            aria-hidden="true"
            className="mx-1 h-[22px] w-px bg-primary-foreground/30"
          />
        )}
        <AuthStatus
          open={openMenu === "account"}
          onOpenChange={setAccountOpen}
        />
      </div>
    </div>
  );
};

export default HeaderToolbar;
